import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const Terms = () => {
  const sections = [
    {
      title: 'Acceptance of Terms',
      content: 'By accessing and using SMR Online, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.'
    },
    {
      title: 'Use License',
      content: 'Permission is granted to temporarily download one copy of SMR Online app for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.'
    },
    {
      title: 'User Account',
      content: 'You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.'
    },
    {
      title: 'Prohibited Activities',
      content: 'Users are prohibited from using the platform for any illegal activities, attempting to gain unauthorized access, or interfering with the proper functioning of the application.'
    },
    {
      title: 'Financial Transactions',
      content: 'All deposits and withdrawals are subject to verification. SMR Online reserves the right to delay or refuse any transaction that appears suspicious or violates our terms.'
    },
    {
      title: 'Limitation of Liability',
      content: 'SMR Online shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.'
    },
    {
      title: 'Modifications',
      content: 'SMR Online reserves the right to modify these terms at any time. We will notify users of any changes by posting the new Terms on this page with an updated effective date.'
    },
    {
      title: 'Contact Information',
      content: 'For any questions about these Terms, please contact us at support@smronline.org.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
      {/* Header */}
      <motion.header
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100 pt-20"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <ArrowLeft className="w-5 h-5 text-primary group-hover:text-primary-dark transition-colors duration-300" />
              <span className="text-primary group-hover:text-primary-dark transition-colors duration-300">Back to Home</span>
            </Link>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gradient">
                Terms & Conditions
              </h1>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-gray-900">
            Terms &{' '}
            <span className="text-gradient">
              Conditions
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Please read these terms carefully before using SMR Online
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Effective Date: 11th October 2025
          </p>
        </motion.div>

        {/* Terms Content */}
        <motion.div
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg border border-gray-100 space-y-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
              {index < sections.length - 1 && (
                <hr className="mt-8 border-gray-100" />
              )}
            </motion.div>
          ))}
        </motion.div>
      </main>
    </div>
  );
};

export default Terms;
